import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";

const PORTRAIT = "https://customer-assets-7cd3h4nn.emergentagent.net/job_golden-wolf-premium/artifacts/0ag6fthu_WhatsApp%20Image%202026-09-01%20at%2021.43.26.jpeg";

const line = {
  hidden: { y: "110%" },
  show: (i = 0) => ({ y: 0, transition: { duration: 1.1, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-[100svh] overflow-hidden" data-testid="hero-section">
      <div className="blob" style={{ width: 520, height: 520, top: "-140px", right: "-120px", background: "radial-gradient(circle, rgba(201,162,75,0.35), transparent 70%)" }} />
      <div className="blob" style={{ width: 480, height: 480, bottom: "-140px", left: "-120px", background: "radial-gradient(circle, rgba(75,46,131,0.28), transparent 70%)" }} />

      {/* Portrait */}
      <motion.div
        style={{ y, scale, opacity }}
        initial={{ clipPath: "polygon(0 100%, 100% 100%, 100% 100%, 0 100%)" }}
        animate={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" }}
        transition={{ duration: 1.6, delay: 0.6, ease: [0.77, 0, 0.175, 1] }}
        className="absolute left-0 top-0 h-full w-full md:w-[52%] lg:w-[48%] spotlight"
        data-testid="hero-portrait"
      >
        <img src={PORTRAIT} alt="Toriano Sanzone — Master Dog Trainer, Wolfkeeper" className="w-full h-full object-cover object-[center_20%] contrast-105 saturate-[1.05]" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-ink/95 md:to-ink/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-[120px] md:pt-[160px] pb-28 md:pb-24 min-h-[100svh] flex flex-col justify-between">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.4 }}
          className="flex items-center gap-4 text-hair text-bone/60"
        >
          <span className="w-8 h-px bg-gold" />
          <span>Chicago · Nairobi · The World</span>
        </motion.div>

        <div className="max-w-4xl md:ml-auto md:pl-8 lg:pl-16">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.3 }} className="text-eyebrow text-gold mb-5">
            Toriano Sanzone · Master Dog Trainer & Founder
          </motion.div>
          <h1 className="font-display text-[clamp(2.6rem,7.4vw,7.2rem)] leading-[0.9] tracking-[-0.03em] text-bone" data-testid="hero-headline">
            <span className="line-mask block"><motion.span variants={line} initial="hidden" animate="show" custom={0}>I train more</motion.span></span>
            <span className="line-mask block"><motion.span variants={line} initial="hidden" animate="show" custom={1}>than dogs.</motion.span></span>
            <span className="line-mask block italic gold-foil"><motion.span variants={line} initial="hidden" animate="show" custom={2}>I build leaders.</motion.span></span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.6 }}
            className="mt-8 max-w-xl text-base md:text-lg text-bone/80 leading-relaxed font-editorial"
          >
            Toriano Sanzone is a <em className="text-gold">third-generation Master Dog Trainer</em>, founder, author, strategist, and cultural architect who has spent more than two decades transforming dogs, families, professionals, businesses, and communities.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.8 }}
            className="mt-6 flex items-center gap-4 text-hair text-bone/70"
          >
            <span className="text-gold">25+</span><span>Years of Leadership</span>
            <span className="text-gold">·</span>
            <span className="text-gold">4,000+</span><span>Dogs Trained</span>
            <span className="text-gold hidden sm:inline">·</span>
            <span className="hidden sm:inline">Chicago to Nairobi</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 2 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <Link to="/dog-training" className="btn-gold" data-testid="hero-cta-training">
              Book Dog Training
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
            </Link>
            <Link to="/work-with-toriano" className="btn-outline" data-testid="hero-cta-hire">Hire Toriano</Link>
            <Link to="/live" className="btn-outline" data-testid="hero-cta-webinar">Join the Free Webinar</Link>
            <Link to="/about" className="text-hair text-bone/70 hover:text-gold self-center underline underline-offset-4" data-testid="hero-cta-story">Discover the Toriano Sanzone story →</Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 2.2 }}
          className="hidden md:grid grid-cols-4 gap-8 pt-10 border-t border-hair"
        >
          {[
            ["3rd", "Generation trainer"],
            ["4,000+", "Dogs transformed"],
            ["25+", "Years of practice"],
            ["1", "Golden Wolf Foundation"],
          ].map(([n, l]) => (
            <div key={l}>
              <div className="stat-num text-3xl md:text-4xl text-bone">{n}</div>
              <div className="text-hair text-bone/50 mt-2">{l}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.5, duration: 1 }}
        className="absolute bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 text-hair text-bone/50 flex flex-col items-center gap-2"
      >
        <span>Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }} className="w-px h-8 bg-gold/60" />
      </motion.div>
    </section>
  );
}
